import Tool, {
  type ToolArgsWithController,
  type ToolArgs,
} from '../../../tools/Tool';
import { Range } from 'slate';
import {
  type CellInfo,
  getTableStructureFromSelection,
} from '../TableStructure';
import TableUtils from '../TableUtils';
import splitCellsSvg from '../../../assets/icons/splitCells.svg';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';

// Split previously merged cells in the current table selection.
class ToolSplitCells extends Tool {
  static toolName = 'Split cells';

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(splitCellsSvg);
  }

  static action({ editor, controller }: ToolArgsWithController): void {
    if (!editor.selection) return;

    const { tableSelection } = controller.state.pluginState;
    if (!tableSelection) return;

    const tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return;

    const tablePath = TableUtils.currentTablePath(editor);
    if (!tablePath) return;

    let mergedCells: CellInfo[] = [];

    if (tableSelection.multipleCellsSelected) {
      mergedCells = tableStructure.mergedCellsInSelection(tableSelection);
    } else {
      const cellPath = TableUtils.currentCellPath(editor);
      if (!cellPath) return;

      const coords = tableStructure.pathToCoordinates(cellPath.slice(-2));
      const cellInfo = tableStructure.cell(coords.columnIndex, coords.rowIndex);
      if (!cellInfo) return;

      if (
        (cellInfo.tableCell.table_colspan ?? 1) > 1 ||
        (cellInfo.tableCell.table_rowspan ?? 1) > 1
      ) {
        mergedCells.push(cellInfo);
      }
    }

    TableUtils.splitCells(editor, tablePath, mergedCells);
  }

  static isEnabled({ editor, controller }: ToolArgs): boolean {
    if (!editor?.selection) return false;
    const { tableSelection } = controller.state.pluginState;

    const tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return false;

    if (
      Range.isCollapsed(editor.selection) ||
      !tableSelection.multipleCellsSelected
    ) {
      const cellPath = TableUtils.currentCellPath(editor);
      if (!cellPath) return false;

      const coords = tableStructure.pathToCoordinates(cellPath.slice(-2));
      const cellInfo = tableStructure.cell(coords.columnIndex, coords.rowIndex);

      if (!cellInfo) return false;

      return (
        (cellInfo.tableCell.table_colspan ?? 1) > 1 ||
        (cellInfo.tableCell.table_rowspan ?? 1) > 1
      );
    }

    return tableStructure.canSplitCells(tableSelection);
  }
}

export default ToolSplitCells;
